import { useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { AnalyticsContext } from "analytics/context";
import { trackEvent } from "analytics/client";
import { sharedLib } from "@shared/services/shared.service";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { isChannelEntry } from "features/telegram/utils";

/**
 * The way to a first bot outside the guided onboarding: the quick guide,
 * the "no exchange connected" prompt, connecting an account (anywhere, the
 * onboarding included) and a bot save the form blocks. A channel post's
 * Set Auto Trading button goes through all of them.
 */
type AccountSetupEvent =
  | "Quick Onboarding - Step Viewed"
  | "Quick Onboarding - Closed"
  | "No Account Prompt - Viewed"
  | "No Account Prompt - Choice"
  | "Connect Account - Viewed"
  | "Connect Account - Method Changed"
  | "Connect Account - Connect Clicked"
  | "Connect Account - Hand-off Failed"
  | "Connect Account - Connected"
  | "Connect Account - Failed"
  | "Bot - Save Blocked";

export type ConnectMethod = "quick_connect" | "api_keys";

/** Where a "no exchange connected" prompt showed. */
export type NoAccountPlacement =
  | "bot_form"
  | "bots_page"
  | "bots_empty"
  | "public_bot"
  | "signal_follow"
  | "trades_empty"
  | "trades_header"
  | "trading_terminal"
  | "marketplace_group"
  | "sub_user"
  | "other";

export interface AccountSetupProperties {
  step?: string;
  step_index?: number;
  steps_total?: number;
  finished?: boolean;
  group_id?: number;
  placement?: NoAccountPlacement;
  choice?: "connect_exchange" | "switch_workspace" | "dismissed";
  workspace?: "demo" | "live";
  /** Inside the guided onboarding, or the account form anywhere else. */
  context?: "onboarding" | "account_form";
  connect_method?: ConnectMethod;
  exchange?: string;
  error_message?: string;
  reason?: string;
  bot_type?: string;
  has_account?: boolean;
  /** The exchange already had this user's Cornix key: no account added. */
  already_connected?: boolean;
}

const ORIGIN_KEY = "cornix_connect_origin";
const ORIGIN_TTL_MS = 30 * 60 * 1000;

/** The prompt that sent the user to connect an account, for the connect
    events that follow (in this tab, for half an hour). */
export const markConnectOrigin = (placement: NoAccountPlacement): void => {
  try {
    sessionStorage.setItem(
      ORIGIN_KEY,
      JSON.stringify({ placement, at: Date.now() })
    );
  } catch {
    return;
  }
};

export const getConnectOrigin = (): NoAccountPlacement | undefined => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(ORIGIN_KEY) || "null");
    if (stored && Date.now() - stored.at < ORIGIN_TTL_MS)
      return stored.placement;
  } catch {
    return undefined;
  }
  return undefined;
};

export const getConnectContext = (
  isOnboarding?: boolean
): AccountSetupProperties["context"] =>
  isOnboarding ? "onboarding" : "account_form";

// Exchanges answer with long texts; keep the part that says why.
const MAX_ERROR_LENGTH = 300;
export const toErrorMessage = (message?: string): string | undefined =>
  message ? message.slice(0, MAX_ERROR_LENGTH) : undefined;

export const useAccountSetupAnalytics = () => {
  const { analytics } = useContext(AnalyticsContext);
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);
  return useCallback(
    (event: AccountSetupEvent, properties: AccountSetupProperties = {}) => {
      if (!analytics || !user?.should_track) return;
      trackEvent(event, {
        platform: "Website",
        is_telegram_mini_app: IS_TELEGRAM_WEB_APP,
        user_in_onboarding: user.is_onboarding ?? null,
        channel_entry: isChannelEntry(),
        ...(event.startsWith("Connect Account - ")
          ? { origin: getConnectOrigin() ?? null }
          : {}),
        ...properties,
      });
    },
    [analytics, user?.should_track, user?.is_onboarding]
  );
};
